import * as React from "react";
import {
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { SiteSortingRatePanel } from "../../sites/SiteSortingRatePanel";
import { formatEquipmentLabel } from "../../utils/formatEquipment";

const API_URL =
  process.env.REACT_APP_API_URL || "https://trackency.hubency.com/api";

function useInviteToken() {
  const qs = new URLSearchParams(window.location.hash.split("?")[1] || "");
  return qs.get("inviteToken") || "";
}

type Row = {
  date: string;
  equivalentFullUnits: number;
  c0: number;
  c25: number;
  c50: number;
  c75: number;
  c100: number;
  c125: number;
};

const SERIES = [
  {
    key: "equivalentFullUnits",
    label: "Équiv. plein",
    color: "#7C3AED",
    thick: true,
  },
  { key: "c0", label: "0%", color: "#6B7280" },
  { key: "c25", label: "25%", color: "#EF4444" },
  { key: "c50", label: "50%", color: "#F59E0B" },
  { key: "c75", label: "75%", color: "#3B82F6" },
  { key: "c100", label: "100%", color: "#10B981" },
  { key: "c125", label: "125%", color: "#EC4899" },
];

export const ExternalStatsPage: React.FC = () => {
  const inviteToken = useInviteToken();
  const [site, setSite] = React.useState<{ id: string; name: string } | null>(
    null
  );
  const [equipments, setEquipments] = React.useState<any[]>([]);
  const [loadingInit, setLoadingInit] = React.useState(true);
  const [errorInit, setErrorInit] = React.useState<string | null>(null);

  const [equipmentId, setEquipmentId] = React.useState<string>("");
  const [from, setFrom] = React.useState<Dayjs>(
    dayjs().subtract(30, "day").startOf("day")
  );
  const [to, setTo] = React.useState<Dayjs>(dayjs().endOf("day"));

  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<Row[]>([]);

  const [hidden, setHidden] = React.useState<Record<string, boolean>>({});

  const LegendClickable: React.FC<any> = (props) => {
    const payload = (props?.payload || []) as Array<{
      value: string;
      color: string;
      dataKey: string;
    }>;
    const ordered = SERIES.map((s) => {
      const p = payload.find((pp) => (pp.dataKey || pp.value) === s.key);
      return {
        ...s,
        color: s.color,
        dataKey: s.key,
        value: s.label,
        exists: !!p,
      };
    });

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        {ordered.map((item) => (
          <Box
            key={item.dataKey}
            onClick={() =>
              setHidden((h) => ({ ...h, [item.dataKey]: !h[item.dataKey] }))
            }
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              cursor: "pointer",
              userSelect: "none",
              bgcolor: hidden[item.dataKey]
                ? "action.hover"
                : "background.paper",
              border: "1px solid",
              borderColor: hidden[item.dataKey] ? "divider" : "transparent",
            }}
            title={hidden[item.dataKey] ? "Afficher" : "Masquer"}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: item.color,
                opacity: hidden[item.dataKey] ? 0.35 : 1,
                display: "inline-block",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                textDecoration: hidden[item.dataKey] ? "line-through" : "none",
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingInit(true);
      try {
        const clRes = await fetch(
          `${API_URL}/site-inventory/invite/client?inviteToken=${inviteToken}`
        );
        if (!clRes.ok) throw new Error("Lien invalide");
        const { site: cl } = await clRes.json();
        if (!alive) return;
        setSite({ id: cl._id, name: cl.name });
        setEquipments(cl.equipments || []);
        if (cl.equipments?.length) setEquipmentId(cl.equipments[0]._id);
      } catch (e: any) {
        if (!alive) return;
        setErrorInit(e.message);
      } finally {
        if (alive) setLoadingInit(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [inviteToken]);

  const fetchStats = React.useCallback(async () => {
    if (!site || !equipmentId) return;
    setLoading(true);
    setErr(null);
    try {
      const url = `${API_URL}/site-inventory/invite/stats/${encodeURIComponent(
        equipmentId
      )}?siteId=${encodeURIComponent(site.id)}&from=${encodeURIComponent(
        from.toDate().toISOString()
      )}&to=${encodeURIComponent(to.toDate().toISOString())}`;
      const res = await fetch(url, {
        headers: inviteToken ? { "X-Invite-Token": inviteToken } : undefined,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Erreur chargement stats");
      setRows((json.data || []) as Row[]);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [site, equipmentId, from, to, inviteToken]);

  React.useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loadingInit)
    return <CircularProgress sx={{ display: "block", m: "auto" }} />;
  if (errorInit) return <Alert severity="error">{errorInit}</Alert>;
  if (!site) return <Alert severity="warning">Site introuvable.</Alert>;

  return (
    <Box>
      <title>Trackency - Statistiques</title>
      <Typography variant="h5" gutterBottom>
        Statistiques — {site.name}
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="eq-select-lbl">Équipement</InputLabel>
                <Select
                  labelId="eq-select-lbl"
                  label="Équipement"
                  value={equipmentId}
                  onChange={(e) => setEquipmentId(String(e.target.value))}
                >
                  {equipments.map((eq) => (
                    <MenuItem key={eq._id} value={eq._id}>
                      {formatEquipmentLabel(eq)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label="Du"
                value={from}
                onChange={(v) => v && setFrom(v.startOf("day"))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label="Au"
                value={to}
                onChange={(v) => v && setTo(v.endOf("day"))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                minDate={from}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ p: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : err ? (
          <Alert severity="error">{err}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">Aucune donnée sur la période.</Alert>
        ) : (
          <ResponsiveContainer width="100%" height={420}>
            <LineChart
              data={rows}
              margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals />
              <Tooltip />
              <Legend
                verticalAlign="top"
                align="left"
                content={<LegendClickable />}
              />
              {SERIES.map((s) =>
                hidden[s.key] ? null : (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={s.thick ? 2.5 : 2}
                    dot={false}
                    activeDot={{ r: s.thick ? 5 : 4 }}
                  />
                )
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <SiteSortingRatePanel siteId={site.id} inviteToken={inviteToken} />
    </Box>
  );
};
